import { FC, useState } from 'react';
import styles from './sso-guide-panel.module.scss';

const SSOGuidePanel: FC = () => {
  const [expandedSections, setExpandedSections] = useState<{
    prerequisites: boolean;
    afterCreating: boolean;
    troubleshooting: boolean;
  }>({
    prerequisites: false,
    afterCreating: false,
    troubleshooting: false,
  });

  const toggleSection = (
    section: 'prerequisites' | 'afterCreating' | 'troubleshooting'
  ) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <div className={styles['guide-panel']}>
      <header className={styles['panel-header']}>
        <h3>SSO Setup Reference Guide</h3>
      </header>

      <div className={styles['panel-content']}>
        {/* Pre-requisites - Collapsible */}
        <section className={styles['section']}>
          <button
            type="button"
            className={styles['section-toggle']}
            onClick={() => toggleSection('prerequisites')}
          >
            <span className={styles['section-title']}>Pre-requisites</span>
            <span className={styles['toggle-icon']}>
              {expandedSections.prerequisites ? '−' : '+'}
            </span>
          </button>
          <p className={styles['section-summary']}>
            One-time setup for each Zitadel instance per GANNET Workspace
            environment (dev/prd): instance settings, project, roles,
            application, and service user.
          </p>
          {expandedSections.prerequisites && (
            <div className={styles['section-details']}>
              <div className={styles['step']}>
                <h4>1. Create Zitadel Instance</h4>
                <ol>
                  <li>
                    Go to{' '}
                    <a
                      href="https://zitadel.com/admin/instances"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      zitadel.cloud
                    </a>{' '}
                    → Create new instance
                  </li>
                  <li>
                    Name the instance for your environment (e.g.,{' '}
                    <code>gannet-workspace-dev</code>,{' '}
                    <code>gannet-workspace-prd</code>)
                  </li>
                </ol>
                <div className={styles['info-box']}>
                  <strong>GDPR Compliance:</strong> Select the appropriate data
                  residency region (EU or Switzerland) based on your compliance
                  requirements.
                </div>
              </div>
              <div className={styles['step']}>
                <h4>2. Disable Login V2 (Instance Level)</h4>
                <ol>
                  <li>Instance Settings → Default Settings → Features</li>
                  <li>
                    Set <strong>Login V2</strong> to <strong>Disable</strong>
                  </li>
                </ol>
              </div>
              <div className={styles['step']}>
                <h4>3. Configure Initial Organization</h4>
                <p className={styles['note']}>
                  The initial org is created with the Zitadel instance. All
                  following setup is done within this org.
                </p>
                <ol>
                  <li>Go to Organization Settings</li>
                  <li>
                    Rename organization to: <code>DFS-SuperAdmin</code>
                  </li>
                </ol>
              </div>
              <div className={styles['step']}>
                <h4>4. Create Project</h4>
                <ol>
                  <li>Go to Zitadel Console → Projects</li>
                  <li>Create New Project</li>
                  <li>
                    Name: <code>gannet-workspace-dev</code> (or{' '}
                    <code>-prd</code>)
                  </li>
                  <li>Check &quot;Assert roles on authentication&quot;</li>
                </ol>
              </div>
              <div className={styles['step']}>
                <h4>5. Create Roles</h4>
                <ul>
                  <li>
                    <code>viewer</code>, <code>user</code>
                  </li>
                  <li>
                    <code>org-data-admin</code>, <code>org-user-admin</code>
                  </li>
                  <li>
                    <code>super-user-admin</code>, <code>super-data-admin</code>
                    , <code>super-org-admin</code>
                  </li>
                </ul>
              </div>
              <div className={styles['step']}>
                <h4>6. Create Application</h4>
                <ol>
                  <li>
                    Name: <code>backend-auth</code>, Type: Web
                  </li>
                  <li>
                    Auth Method: None (PKCE), Grant Types: Auth Code + Refresh
                    Token
                  </li>
                  <li>
                    Token Settings: Bearer, Include profile info in ID Token ✅
                  </li>
                </ol>
                <div className={styles['info-box']}>
                  <strong>Redirect URIs (Dev):</strong>
                  <ul>
                    <li>
                      <code>http://localhost:3000/auth/sso/callback</code>
                    </li>
                    <li>
                      <code>
                        https://*.dev.workspace.gannet.ai/auth/sso/callback
                      </code>
                    </li>
                  </ul>
                  <strong>Redirect URIs (Prd):</strong>
                  <ul>
                    <li>
                      <code>http://localhost:3000/auth/sso/callback</code>
                    </li>
                    <li>
                      <code>
                        https://*.workspace.gannet.ai/auth/sso/callback
                      </code>
                    </li>
                  </ul>
                  <strong>Post Logout URIs (Dev):</strong>
                  <ul>
                    <li>
                      <code>http://localhost:3000/</code>
                    </li>
                    <li>
                      <code>https://*.dev.workspace.gannet.ai/</code>
                    </li>
                    <li>
                      <code>https://dev.workspace.gannet.ai/</code>
                    </li>
                  </ul>
                  <strong>Post Logout URIs (Prd):</strong>
                  <ul>
                    <li>
                      <code>http://localhost:3000/</code>
                    </li>
                    <li>
                      <code>https://*.workspace.gannet.ai/</code>
                    </li>
                    <li>
                      <code>https://workspace.gannet.ai/</code>
                    </li>
                  </ul>
                </div>
              </div>
              <div className={styles['step']}>
                <h4>7. Create Service User</h4>
                <ol>
                  <li>Users → Service Users → + New</li>
                  <li>
                    Name: <code>gannet-workspace-backend</code>, Token Type:
                    Bearer
                  </li>
                  <li>
                    Default Settings (+): Add <strong>IAM_OWNER_VIEWER</strong>,{' '}
                    <strong>IAM_USER_MANAGER</strong> for instance level roles
                  </li>
                  <li>
                    Organization tab (+): Add <strong>ORG_OWNER</strong> for
                    organization level roles
                  </li>
                  <li>
                    Personal Access Tokens → Create and save securely, need to
                    be set within the backend AWS Secret Manager
                  </li>
                </ol>
              </div>
            </div>
          )}
        </section>

        {/* Setup Steps - Always Expanded */}
        <section className={styles['section']}>
          <div className={styles['section-header-static']}>
            <span className={styles['section-title']}>Setup Steps</span>
            <span className={styles['badge']}>Reference</span>
          </div>
          <p className={styles['section-summary']}>
            Complete these steps in Zitadel Console. Check off each step on the
            form as you complete it.
          </p>

          <div className={styles['section-details']}>
            <div className={styles['step']}>
              <h4>Create Organization in Zitadel</h4>
              <ol>
                <li>Go to Zitadel Console</li>
                <li>
                  Click org dropdown → &quot;+ Create new organization&quot;
                </li>
                <li>
                  Enter organization name (all lowercase, e.g.,{' '}
                  <code>example-corp</code>)
                </li>
                <li>Choose &quot;Use your personal account as owner&quot;</li>
              </ol>
              <div className={styles['info-box']}>
                <strong>Copy the Organization ID</strong> from Settings or URL
              </div>
            </div>

            <div className={styles['step']}>
              <h4>Add Yourself as ORG_OWNER</h4>
              <p className={styles['note']}>
                Skip if you selected &quot;Use your personal account&quot;
                above.
              </p>
              <ol>
                <li>Go to Organization Settings → Managers</li>
                <li>Add your user (Zitadel admin user) with role: ORG_OWNER</li>
              </ol>
            </div>

            <div className={styles['step']}>
              <h4>Configure Identity Provider</h4>
              <ol>
                <li>Go to Organization Settings → Identity Providers</li>
                <li>Click + New, select template (Okta/Azure AD/Google)</li>
                <li>Enter customer&apos;s IdP credentials</li>
                <li>Enable: Auto creation, Auto update, Account linking</li>
                <li>Save and Activate</li>
              </ol>
            </div>

            <div className={styles['step']}>
              <h4>Update Login Policy</h4>
              <ol>
                <li>Go to Organization Settings → Login Behavior</li>
                <li>Enable: External IDP allowed</li>
                <li>Disable: Username Password allowed</li>
                <li>Disable: Domain Discovery allowed</li>
              </ol>
            </div>

            <div className={styles['step']}>
              <h4>Grant Central Project to New Organization</h4>
              <ol>
                <li>Switch to DFS-SuperAdmin organization</li>
                <li>
                  Projects → <code>gannet-workspace-dev(-prd)</code> → Grants →
                  + New Organization
                </li>
                <li>Search for customer org, select roles to grant</li>
              </ol>
            </div>

            <div className={styles['step']}>
              <h4>Add Subdomain in Cloudflare Pages</h4>
              <p className={styles['note']}>
                Required for the SSO URL to work. Cloudflare Pages does not
                support wildcard domains.
              </p>
              <ol>
                <li>
                  Go to{' '}
                  <a
                    href="https://dash.cloudflare.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Cloudflare Dashboard
                  </a>{' '}
                  → Compute & AI {'>'} Workers & Pages → Project
                </li>
                <li>Custom domains → Set up a custom domain</li>
                <li>
                  Enter: <code>&#123;subdomain&#125;.workspace.gannet.ai</code>
                </li>
                <li>
                  For dev:{' '}
                  <code>&#123;subdomain&#125;.dev.workspace.gannet.ai</code>
                </li>
                <li>Wait for DNS propagation (~1-5 min)</li>
              </ol>
            </div>
          </div>
        </section>

        {/* After Creating Org - Collapsible */}
        <section className={styles['section']}>
          <button
            type="button"
            className={styles['section-toggle']}
            onClick={() => toggleSection('afterCreating')}
          >
            <span className={styles['section-title']}>After Creating Org</span>
            <span className={styles['toggle-icon']}>
              {expandedSections.afterCreating ? '−' : '+'}
            </span>
          </button>
          <p className={styles['section-summary']}>
            After creating the org here, test SSO in an incognito window.
          </p>
          {expandedSections.afterCreating && (
            <div className={styles['section-details']}>
              <ol>
                <li>Open incognito browser</li>
                <li>
                  Go to: https://&#123;subdomain&#125;.workspace.gannet.ai
                </li>
                <li>Click &quot;Continue with SSO&quot;</li>
                <li>Authenticate via customer IdP</li>
                <li>Should land in GANNET Workspace</li>
              </ol>
            </div>
          )}
        </section>

        {/* Troubleshooting - Collapsible */}
        <section className={styles['section']}>
          <button
            type="button"
            className={styles['section-toggle']}
            onClick={() => toggleSection('troubleshooting')}
          >
            <span className={styles['section-title']}>Troubleshooting</span>
            <span className={styles['toggle-icon']}>
              {expandedSections.troubleshooting ? '−' : '+'}
            </span>
          </button>
          {expandedSections.troubleshooting && (
            <div className={styles['section-details']}>
              <div className={styles['troubleshoot-item']}>
                <h4>&quot;Organization not found&quot;</h4>
                <p>Verify Zitadel Organization ID is correct</p>
              </div>
              <div className={styles['troubleshoot-item']}>
                <h4>User can&apos;t log in</h4>
                <p>Check IdP config, Login Policy, Project Grant</p>
              </div>
              <div className={styles['troubleshoot-item']}>
                <h4>&quot;Invalid redirect URI&quot;</h4>
                <p>Check subdomain and wildcard Redirect URIs</p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default SSOGuidePanel;
